import {useMemo} from 'react'
import {useQuery, type QueryKey} from '@github-ui/react-query'
import {type FileTreeResponse, FileTreeResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_tree'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {createProtoPersister} from '@github-ui/react-query-persister'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {useAnalytics} from '@github-ui/use-analytics'

function fileTreeQueryKey(basePath: string): QueryKey {
  return [PageData.fileTree, basePath]
}

function fileTreeApiUrl(basePath: string): string {
  return `${basePath}/page_data/${PageData.fileTree}`
}

/**
 * Lazily fetches the file_tree endpoint and hydrates the layout store with
 * the diff path order map. Intended for use when the comments side panel opens
 * on a page that doesn't have files-changed route data (e.g. conversation tab).
 */
export function useFileTreeDiffSummaries<T = FileTreeResponse>({
  basePath,
  select,
  enabled,
}: {
  basePath: string
  select?: (data: FileTreeResponse) => T
  enabled?: boolean
}) {
  const {sendAnalyticsEvent} = useAnalytics()

  // The file_tree endpoint is schema-bound proto, so its decoded response can be
  // persisted to IndexedDB (validated against the schema, keyed by its hash) to
  // show cached diff summaries immediately while revalidating in the background.
  const persister = useMemo(
    () => createProtoPersister({schema: FileTreeResponseSchema, sendAnalyticsEvent}),
    [sendAnalyticsEvent],
  )

  const query = useQuery({
    queryKey: fileTreeQueryKey(basePath),
    queryFn: async () => {
      return await queryFnFetch(
        {queryDeps: {pathname: fileTreeApiUrl(basePath)}, routeId: 'pull-requests', queryName: 'fileTree'},
        {schema: FileTreeResponseSchema},
      )
    },
    staleTime: 60_000,
    persister,
    select,
    enabled,
  })

  return query
}
